import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { combineLatest, firstValueFrom, Subject, takeUntil } from 'rxjs'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { FeatureCollection, Point } from 'geojson'
import { XyzTileModel } from '../models/xyz-tile.model'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { ClusteredSignProperties } from '../models/clustered-sign-model'
import { times } from 'lodash'

@Injectable()
export class DataClusteredSignsService {
  private clusteredSigns: FeatureCollection<Point, ClusteredSignProperties> = {
    type: 'FeatureCollection',
    features: [],
  }
  public isDataInitialized = false

  // Add request cancellation mechanism
  private cancelPreviousRequest$ = new Subject<void>()

  constructor(
    private readonly http: HttpClient,
    private appState: AppStateService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {}

  async loadClusteredSignsData(
    isHistoryEnabled: boolean,
    xyzTiles: XyzTileModel[],
    timestamp?: Date,
    bounds?: maplibregl.LngLatBounds,
  ) {
    // Cancel any previous ongoing request
    this.cancelPreviousRequest$.next()

    this.isDataInitialized = true

    if (isHistoryEnabled) {
      // If history is enabled, we need to load clustered signs based on the selected timestamp and map bounds
      if (timestamp && bounds) {
        const clusteredSigns = await this.loadClusteredSignsForHistory(timestamp, bounds)
        this.clusteredSigns.features = clusteredSigns.features
      }
    } else if (!isHistoryEnabled && xyzTiles) {
      const clusteredSignsRequests$ = xyzTiles.map(async (tile) => {
        let clusteredSigns: FeatureCollection<Point, ClusteredSignProperties> | undefined =
          undefined

        clusteredSigns = await this.loadClusteredSigns(tile)

        return clusteredSigns
      })
      const clusteredSigns$ = combineLatest(clusteredSignsRequests$)
      const clusteredSignsEvents = await firstValueFrom(clusteredSigns$)
      this.clusteredSigns.features = []

      clusteredSignsEvents.map((clusteredSigns) => {
        if (clusteredSigns) {
          this.clusteredSigns.features = this.clusteredSigns.features.concat(
            clusteredSigns.features,
          )
        }
      })
    }

    return this
  }

  async loadClusteredSignsForHistory(
    timestamp: Date,
    bounds: maplibregl.LngLatBounds,
  ): Promise<FeatureCollection<Point, ClusteredSignProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
      params: {
        timestamp: timestamp.toISOString(),
        minLat: bounds.getSouth().toString(),
        maxLat: bounds.getNorth().toString(),
        minLng: bounds.getWest().toString(),
        maxLng: bounds.getEast().toString(),
      },
    }
    const url = new URL(`${this.config.backend_url}vzo/clustered-signs/historic`)

    const result = await firstValueFrom(
      this.http
        .get<FeatureCollection<Point, ClusteredSignProperties>>(url.toString(), httpOptions)
        .pipe(takeUntil(this.cancelPreviousRequest$)),
    )
    return result
  }

  async loadClusteredSigns(
    xyzTiles: XyzTileModel,
  ): Promise<FeatureCollection<Point, ClusteredSignProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
    }
    const url = new URL(
      `${this.config.backend_url}vzo/clustered-signs/${xyzTiles.z}/${xyzTiles.x}/${xyzTiles.y}`,
    )

    // const url = `${location.origin}/assets/testdata/rcs-heavy-rain.json`
    const result = await firstValueFrom(
      this.http
        .get<FeatureCollection<Point, ClusteredSignProperties>>(url.toString(), httpOptions)
        .pipe(takeUntil(this.cancelPreviousRequest$)),
    )
    return result
  }

  getClusteredSigns(selectedFeatureId: string): FeatureCollection<Point, ClusteredSignProperties> {
    return this.clusteredSigns
  }
  // Method to manually cancel current requests (if needed by external services)
  public cancelCurrentRequests(): void {
    this.cancelPreviousRequest$.next()
  }

  // Clean up on service destruction
  ngOnDestroy(): void {
    this.cancelPreviousRequest$.next()
    this.cancelPreviousRequest$.complete()
  }
}
